import { inGerman, OFFLINE_MESSAGE, unexpectedStatus } from "./messages";
import type {
  ChannelList,
  ErrorLogEntry,
  ErrorLogQuery,
  Highlight,
  NewVideo,
  Page,
  Picture,
  PictureChanges,
  PreviousChoice,
  SessionResponse,
  SetHighlight,
  Settings,
  SettingsChanges,
  Show,
  ShowInput,
  Track,
  TrackSuggestion,
  Video,
  VideoChanges,
  VideoSuggestion,
} from "./types";

/** Same prefix the server mounts the admin API on. */
const BASE = "/api/admin";

const CSRF_HEADER = "X-CSRF-Token";

/** The methods the server's CsrfGuard lets through without a token. */
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * A failed request, with the server's own wording already translated.
 *
 * `messages` is a list because class-validator reports one entry per rejected
 * field; everything else produces exactly one.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly messages: string[],
  ) {
    super(messages[0] ?? unexpectedStatus(status));
    this.name = "ApiError";
  }

  /** Signed out, or never signed in. */
  get isUnauthenticated(): boolean {
    // 403 is the CSRF guard. In practice it means the session was replaced
    // underneath us, so it needs the same answer as a 401: sign in again.
    return this.status === 401 || this.status === 403;
  }
}

/**
 * The CSRF token, held here rather than in a component.
 *
 * It belongs to the connection, not to a screen: every write carries it, and
 * signing in or out replaces it because passport starts a new session.
 */
let csrfToken = "";

export function rememberCsrfToken(token: string): void {
  csrfToken = token;
}

export function currentCsrfToken(): string {
  return csrfToken;
}

/**
 * Called whenever the server says the session is gone, so the shell can ask
 * for a new sign-in without every caller having to think about it.
 */
let onUnauthenticated: (() => void) | null = null;

export function whenUnauthenticated(handler: () => void): void {
  onUnauthenticated = handler;
}

type Query = Record<string, string | number | undefined>;

function queryString(query: Query | undefined): string {
  if (!query) {
    return "";
  }

  const parameters = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") {
      parameters.set(key, String(value));
    }
  }

  const rendered = parameters.toString();
  return rendered ? `?${rendered}` : "";
}

function messagesFrom(payload: unknown, status: number): string[] {
  if (payload && typeof payload === "object" && "message" in payload) {
    const message = (payload as { message: unknown }).message;

    if (Array.isArray(message)) {
      const texts = message.filter(
        (entry): entry is string => typeof entry === "string",
      );
      if (texts.length > 0) {
        return texts.map(inGerman);
      }
    }

    if (typeof message === "string" && message !== "") {
      return [inGerman(message)];
    }
  }

  return [unexpectedStatus(status)];
}

interface RequestOptions {
  body?: unknown;
  query?: Query;
}

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  let body: BodyInit | undefined;

  if (options.body instanceof FormData) {
    // No Content-Type: the browser has to set the multipart boundary itself.
    body = options.body;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  if (!SAFE_METHODS.has(method)) {
    headers[CSRF_HEADER] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}${queryString(options.query)}`, {
      method,
      headers,
      body,
      // The session cookie. Same origin as the admin itself, but spelled out.
      credentials: "same-origin",
    });
  } catch {
    // Network failure, not an answer: there is no status to report.
    throw new ApiError(0, [OFFLINE_MESSAGE]);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new ApiError(
      response.status,
      messagesFrom(payload, response.status),
    );
    if (error.isUnauthenticated) {
      onUnauthenticated?.();
    }
    throw error;
  }

  return payload as T;
}

/**
 * The channel list carries the server's own English inside a successful
 * answer: when YouTube cannot be reached the request is still a 200, so
 * `messagesFrom` - which only ever sees a failed one - never touches it.
 * Translating it here keeps messages.ts the single place where anything the
 * server says turns into German, instead of leaving one sentence in English
 * for the one case in which she most needs to read it.
 */
function withGermanMessage(list: ChannelList): ChannelList {
  if (!list.message) {
    return list;
  }

  return { ...list, message: inGerman(list.message) };
}

/**
 * The whole admin API, one function per endpoint.
 *
 * Sign-in and sign-out take the token the server hands back: passport starts a
 * new session for both, which invalidates the one held here.
 */
export const api = {
  async csrfToken(): Promise<string> {
    const { csrfToken: token } = await request<{ csrfToken: string }>(
      "GET",
      "/csrf-token",
    );
    rememberCsrfToken(token);
    return token;
  },

  async session(): Promise<SessionResponse> {
    const session = await request<SessionResponse>("GET", "/session");
    rememberCsrfToken(session.csrfToken);
    return session;
  },

  async signIn(username: string, password: string): Promise<SessionResponse> {
    const session = await request<SessionResponse>("POST", "/session", {
      body: { username, password },
    });
    rememberCsrfToken(session.csrfToken);
    return session;
  },

  async signOut(): Promise<void> {
    const { csrfToken: token } = await request<{ csrfToken: string }>(
      "DELETE",
      "/session",
    );
    rememberCsrfToken(token);
  },

  listShows(page: number, limit: number): Promise<Page<Show>> {
    return request<Page<Show>>("GET", "/shows", { query: { page, limit } });
  },

  getShow(id: string): Promise<Show> {
    return request<Show>("GET", `/shows/${id}`);
  },

  createShow(input: ShowInput): Promise<Show> {
    return request<Show>("POST", "/shows", { body: input });
  },

  updateShow(id: string, changes: Partial<ShowInput>): Promise<Show> {
    return request<Show>("PATCH", `/shows/${id}`, { body: changes });
  },

  deleteShow(id: string): Promise<void> {
    return request<void>("DELETE", `/shows/${id}`);
  },

  listPictures(): Promise<Picture[]> {
    return request<Picture[]>("GET", "/pictures");
  },

  createPicture(file: File, alt: string, show: boolean): Promise<Picture> {
    const form = new FormData();
    form.append("file", file);
    form.append("alt", alt);
    form.append("show", String(show));
    return request<Picture>("POST", "/pictures", { body: form });
  },

  updatePicture(id: string, changes: PictureChanges): Promise<Picture> {
    return request<Picture>("PATCH", `/pictures/${id}`, { body: changes });
  },

  deletePicture(id: string): Promise<void> {
    return request<void>("DELETE", `/pictures/${id}`);
  },

  reorderPictures(ids: string[]): Promise<Picture[]> {
    return request<Picture[]>("PUT", "/pictures/order", { body: { ids } });
  },

  listVideos(): Promise<Video[]> {
    return request<Video[]>("GET", "/videos");
  },

  async channelVideos(): Promise<ChannelList> {
    return withGermanMessage(
      await request<ChannelList>("GET", "/videos/channel"),
    );
  },

  /**
   * Fetches the channel list at YouTube again instead of taking the server's
   * cached one. A POST because it costs quota at YouTube, and a GET has to
   * stay something that can be repeated without spending anything.
   */
  async refreshChannelVideos(): Promise<ChannelList> {
    return withGermanMessage(
      await request<ChannelList>("POST", "/videos/channel/refresh"),
    );
  },

  lookupVideo(url: string): Promise<VideoSuggestion> {
    return request<VideoSuggestion>("POST", "/videos/lookup", {
      body: { url },
    });
  },

  createVideo(input: NewVideo): Promise<Video> {
    return request<Video>("POST", "/videos", { body: input });
  },

  updateVideo(id: string, changes: VideoChanges): Promise<Video> {
    return request<Video>("PATCH", `/videos/${id}`, { body: changes });
  },

  deleteVideo(id: string): Promise<void> {
    return request<void>("DELETE", `/videos/${id}`);
  },

  reorderVideos(ids: string[]): Promise<Video[]> {
    return request<Video[]>("PUT", "/videos/order", { body: { ids } });
  },

  listTracks(): Promise<Track[]> {
    return request<Track[]>("GET", "/tracks");
  },

  lookupTrack(url: string): Promise<TrackSuggestion> {
    return request<TrackSuggestion>("POST", "/tracks/lookup", {
      body: { url },
    });
  },

  createTrack(url: string): Promise<Track> {
    return request<Track>("POST", "/tracks", { body: { url } });
  },

  updateTrack(id: string, visible: boolean): Promise<Track> {
    return request<Track>("PATCH", `/tracks/${id}`, { body: { visible } });
  },

  deleteTrack(id: string): Promise<void> {
    return request<void>("DELETE", `/tracks/${id}`);
  },

  reorderTracks(ids: string[]): Promise<Track[]> {
    return request<Track[]>("PUT", "/tracks/order", { body: { ids } });
  },

  getHighlight(): Promise<Highlight> {
    return request<Highlight>("GET", "/highlight");
  },

  /**
   * Points the highlight at something else.
   *
   * `previous` says what is to happen to the object it lets go of, and is only
   * sent when the server has said that object would be left with nowhere to be
   * found - `wouldOrphan` on the highlight being replaced.
   */
  setHighlight(
    input: SetHighlight,
    previous?: PreviousChoice,
  ): Promise<Highlight> {
    return request<Highlight>("PUT", "/highlight", {
      body: input,
      query: { previous },
    });
  },

  uploadHighlightPicture(
    file: File,
    alt: string,
    inMedia: boolean,
    title: string,
    previous?: PreviousChoice,
  ): Promise<Highlight> {
    const form = new FormData();
    form.append("file", file);
    form.append("alt", alt);
    form.append("inMedia", String(inMedia));
    form.append("title", title);
    return request<Highlight>("POST", "/highlight/picture", {
      body: form,
      query: { previous },
    });
  },

  setHighlightTitle(title: string): Promise<Highlight> {
    return request<Highlight>("PATCH", "/highlight", { body: { title } });
  },

  removeHighlight(previous?: PreviousChoice): Promise<void> {
    return request<void>("DELETE", "/highlight", { query: { previous } });
  },

  changePassword(oldPassword: string, newPassword: string): Promise<string> {
    return request<{ message: string }>("POST", "/password", {
      body: { oldPassword, newPassword },
    }).then(({ message }) => inGerman(message));
  },

  getSettings(): Promise<Settings> {
    return request<Settings>("GET", "/settings");
  },

  updateSettings(changes: SettingsChanges): Promise<Settings> {
    return request<Settings>("PATCH", "/settings", { body: changes });
  },

  listErrors(query: ErrorLogQuery): Promise<Page<ErrorLogEntry>> {
    return request<Page<ErrorLogEntry>>("GET", "/error-log", {
      query: {
        page: query.page,
        limit: query.limit,
        from: query.from,
        to: query.to,
        operation: query.operation,
      },
    });
  },
};
